import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Send, Download, Trash2, Copy, Link as LinkIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";
import LineItemsEditor from "@/components/LineItemsEditor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const QUOTE_STATUSES = ["Draft", "Sent", "Approved"];

const blank = {
  client_name: "",
  client_phone: "",
  client_email: "",
  event_date: "",
  event_time: "",
  event_location: "",
  catering_food: false,
  catering_mocktail: false,
  catering_hookah: false,
  menu_description: "",
  guest_count: 0,
  per_person_price: 0,
  service_charge: 0,
  tax: 0,
  addons: "",
  notes: "",
  validity_days: 14,
  line_items: [],
};

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "col-span-1 md:col-span-2" : ""} data-testid={testid}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b border-white/15 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white placeholder-white/25 transition-colors";
const boxInputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2.5 focus:border-[#C9963F] focus:outline-none text-white placeholder-white/25 transition-colors";

const STATUS_STYLES = {
  Draft: "text-gray-300 bg-gray-400/10 border-gray-400/30",
  Sent: "text-blue-300 bg-blue-400/10 border-blue-400/30",
  Approved: "text-[#C9963F] bg-[#C9963F]/10 border-[#C9963F]/40",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return <span className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${cls}`}>{status}</span>;
}

export default function QuoteEditor({ mode }) {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${id}`);
        setQuote(data);
        // Migrate legacy fields → line_items if needed
        const merged = { ...blank, ...data };
        if (!merged.line_items || merged.line_items.length === 0) {
          const seeded = [];
          if (Number(data.per_person_price) > 0) {
            seeded.push({ id: "lg_pp", label: "Per Person", kind: "per_person", quantity: 1, unit_price: Number(data.per_person_price) });
          }
          if (Number(data.service_charge) > 0) {
            seeded.push({ id: "lg_sc", label: "Service Charge", kind: "flat", quantity: 1, unit_price: Number(data.service_charge) });
          }
          if (seeded.length) merged.line_items = seeded;
        }
        setForm(merged);
      } catch { toast.error("Could not load quote"); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  // Load internal menu for the picker
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/menu-items"); setMenuItems(data || []); } catch {}
    })();
  }, []);

  const totals = useMemo(() => {
    const gc = Number(form.guest_count) || 0;
    const items = form.line_items || [];
    let subtotal = 0;
    if (items.length) {
      for (const it of items) {
        const up = Number(it.unit_price) || 0;
        if (it.kind === "per_person") subtotal += gc * up;
        else subtotal += (Number(it.quantity) || 0) * up;
      }
    } else {
      subtotal = gc * (Number(form.per_person_price) || 0) + (Number(form.service_charge) || 0);
    }
    const total = subtotal + (Number(form.tax) || 0);
    return { subtotal, total };
  }, [form.guest_count, form.line_items, form.per_person_price, form.service_charge, form.tax]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.client_name?.trim()) return toast.error("Client name is required");
    if (!form.event_date) return toast.error("Event date is required");
    setSaving(true);
    try {
      const items = (form.line_items || []).map((it) => ({
        id: it.id,
        label: it.label || "",
        kind: it.kind || "flat",
        quantity: Number(it.quantity) || (it.kind === "per_person" ? 1 : 1),
        unit_price: Number(it.unit_price) || 0,
      }));
      const payload = {
        ...form,
        guest_count: Number(form.guest_count) || 0,
        per_person_price: items.length ? 0 : (Number(form.per_person_price) || 0),
        service_charge: items.length ? 0 : (Number(form.service_charge) || 0),
        tax: Number(form.tax) || 0,
        validity_days: Number(form.validity_days) || 14,
        line_items: items,
      };
      if (isEdit) {
        const { data } = await api.put(`/quotes/${id}`, payload);
        setQuote(data); toast.success("Quote updated");
      } else {
        const { data } = await api.post("/quotes", payload);
        toast.success("Quote created");
        navigate(`/admin/quotes/${data.id}`, { replace: true });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  }

  async function changeStatus(s) {
    try { const { data } = await api.patch(`/quotes/${id}/status`, { status: s }); setQuote(data); toast.success(`Status: ${s}`); }
    catch { toast.error("Failed"); }
  }

  async function sendNow() {
    try {
      const { data } = await api.post(`/quotes/${id}/send`);
      setQuote(data.quote);
      const link = `${window.location.origin}/q/${data.public_token}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Quote sent. Link copied.");
    } catch { toast.error("Failed"); }
  }

  async function copyLink() {
    const link = `${window.location.origin}/q/${quote.public_token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  }

  async function downloadPdf() {
    try {
      const res = await fetch(`${API}/quotes/${id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sheesh-quote-${(form.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download"); }
  }

  async function deleteQuote() {
    if (!window.confirm("Delete this quote?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Deleted"); navigate("/admin/quotes", { replace: true }); }
    catch { toast.error("Failed"); }
  }

  async function generateContract() {
    try {
      const { data } = await api.post(`/quotes/${id}/generate-contract`);
      toast.success(data.already_exists ? "Contract already exists" : "Contract created");
      navigate(`/admin/contracts/${data.contract_id}`);
    } catch { toast.error("Failed to generate contract"); }
  }

  function insertMenuItem(item) {
    const line = `${item.name}${item.description ? ` — ${item.description}` : ""}`;
    set("menu_description", form.menu_description ? form.menu_description + "\n" + line : line);
    toast.success(`Added: ${item.name}`);
  }

  if (loading) return <div className="text-white/40">Loading…</div>;

  return (
    <div data-testid="quote-editor-page">
      <Link to="/admin/quotes" className="inline-flex items-center gap-2 text-white/50 hover:text-[#C9963F] text-sm mb-6">
        <ArrowLeft size={14} /> Back to quotes
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">{isEdit ? "Edit Quote" : "New Quote"}</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">{form.client_name || "New Quote"}</h1>
          {isEdit && quote && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span data-testid="quote-editor-status-badge"><StatusBadge status={quote.status} /></span>
              {quote.expires_at && <span className="text-[10px] uppercase tracking-wider text-white/40">Valid until {new Date(quote.expires_at).toLocaleDateString()}</span>}
              {quote.converted_contract_id && (
                <Link to={`/admin/contracts/${quote.converted_contract_id}`} className="text-[10px] uppercase tracking-wider text-[#C9963F] hover:underline">
                  → Linked contract
                </Link>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isEdit && (
            <>
              {quote?.status === "Approved" && !quote?.converted_contract_id && (
                <button onClick={generateContract} className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium hover:bg-[#A67A2D] px-3 py-2 text-sm" data-testid="generate-contract-btn">
                  <FileText size={14} /> Generate Contract
                </button>
              )}
              <button onClick={sendNow} className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-2 text-sm" data-testid="send-quote-btn">
                <Send size={14} /> Send
              </button>
              <button onClick={copyLink} className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2 text-sm" data-testid="quote-copy-link-btn">
                <LinkIcon size={14} /> Copy Link
              </button>
              <button onClick={downloadPdf} className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-3 py-2 text-sm" data-testid="quote-pdf-btn">
                <Download size={14} /> PDF
              </button>
              <button onClick={deleteQuote} className="inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 px-3 py-2 text-sm">
                <Trash2 size={14} />
              </button>
            </>
          )}
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium hover:bg-[#A67A2D] px-4 py-2 text-sm disabled:opacity-50" data-testid="save-quote-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {isEdit && quote && (
        <div className="bg-[#0e0e0e] border border-white/10 p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="quote-status-bar">
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">Status</span>
          <Select value={QUOTE_STATUSES.includes(quote.status) ? quote.status : undefined} onValueChange={changeStatus}>
            <SelectTrigger className="w-44 bg-[#0a0a0a] border-white/15 text-white rounded-none focus:ring-[#C9963F]" data-testid="quote-status-select">
              <SelectValue placeholder="Set status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0e0e0e] border-white/15 text-white rounded-none">
              {QUOTE_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`quote-status-option-${s}`} className="focus:bg-[#C9963F]/15 focus:text-[#C9963F]">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {quote.signed_at ? (
            <span className="text-xs text-[#C9963F]" data-testid="quote-signed-info">
              Signed by {quote.signed_by_name} on {new Date(quote.signed_at).toLocaleString()}
            </span>
          ) : (
            <span className="text-xs text-white/40" data-testid="quote-unsigned-info">Not signed by client yet</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-8">
          <Section title="Client">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Client Name *" testid="qfield-client-name"><input className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="qinput-client-name" /></Field>
              <Field label="Phone"><input className={inputCls} value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} /></Field>
              <Field label="Email" full><input type="email" className={inputCls} value={form.client_email} onChange={(e) => set("client_email", e.target.value)} /></Field>
            </div>
          </Section>

          <Section title="Event">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="Event Date *"><input type="date" className={boxInputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="qinput-event-date" /></Field>
              <Field label="Event Time"><input type="time" className={boxInputCls} value={form.event_time} onChange={(e) => set("event_time", e.target.value)} /></Field>
              <Field label="Location" full><input className={inputCls} value={form.event_location} onChange={(e) => set("event_location", e.target.value)} /></Field>
            </div>
          </Section>

          <Section title="Catering">
            <div className="flex flex-wrap gap-3 mb-5">
              {[["catering_food", "Food Catering"], ["catering_mocktail", "Mocktail Bar"], ["catering_hookah", "Hookah Service"]].map(([k, label]) => (
                <label key={k} className={`cursor-pointer px-4 py-2.5 border text-sm select-none ${form[k] ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                  <input type="checkbox" className="hidden" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} />{label}
                </label>
              ))}
            </div>

            {menuItems.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Quick add from internal menu</div>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {menuItems.filter((m) => m.active).map((m) => (
                    <button type="button" key={m.id} onClick={() => insertMenuItem(m)} className="px-2.5 py-1 text-xs border border-white/15 text-white/70 hover:border-[#C9963F] hover:text-[#C9963F]">
                      + {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Field label="Menu / Package Description"><textarea rows={5} className={boxInputCls} value={form.menu_description} onChange={(e) => set("menu_description", e.target.value)} placeholder="What's included in this quote…" /></Field>
            <Field label="Add-ons / Service Type"><textarea rows={3} className={boxInputCls} value={form.addons} onChange={(e) => set("addons", e.target.value)} /></Field>
          </Section>

          <Section title="Pricing Estimate">
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-5">
              <Field label="Guest Count"><input type="number" min="0" className={boxInputCls} value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} data-testid="qinput-guest-count" /></Field>
              <Field label="Tax ($)"><input type="number" step="0.01" min="0" className={boxInputCls} value={form.tax} onChange={(e) => set("tax", e.target.value)} /></Field>
            </div>
            <LineItemsEditor
              items={form.line_items || []}
              guestCount={form.guest_count}
              onChange={(items) => set("line_items", items)}
              testidPrefix="quote-li"
            />
          </Section>

          <Section title="Quote Settings & Notes">
            <Field label="Validity (days)"><input type="number" min="1" className={boxInputCls} value={form.validity_days} onChange={(e) => set("validity_days", e.target.value)} /></Field>
            <Field label="Internal notes (not shown to client)"><textarea rows={3} className={boxInputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </Section>
        </div>

        <aside>
          <div className="bg-[#0e0e0e] border border-white/10 p-6 sticky top-24">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-4">Estimate</div>
            <Row label="Guests" value={form.guest_count || 0} />
            {(form.line_items || []).map((it, idx) => {
              const up = Number(it.unit_price) || 0;
              const qty = it.kind === "per_person" ? (Number(form.guest_count) || 0) : (Number(it.quantity) || 0);
              const line = qty * up;
              return (
                <Row key={it.id || idx} label={it.label || (it.kind === "per_person" ? "Per Person" : "Item")} value={`$${line.toFixed(2)}`} />
              );
            })}
            {(!form.line_items || form.line_items.length === 0) && (
              <>
                <Row label="Per Person" value={`$${Number(form.per_person_price || 0).toFixed(2)}`} />
                <Row label="Service" value={`$${Number(form.service_charge || 0).toFixed(2)}`} />
              </>
            )}
            <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
            <Row label="Tax" value={`$${Number(form.tax || 0).toFixed(2)}`} />
            <div className="h-px bg-[#C9963F]/40 my-3" />
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">Estimated Total</span>
              <span className="font-serif-brand text-3xl text-[#C9963F]" data-testid="quote-summary-total">${totals.total.toFixed(2)}</span>
            </div>
            {isEdit && quote?.public_token && (
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Customer link</div>
                <button onClick={copyLink} className="w-full flex items-center justify-between gap-2 bg-[#0a0a0a] border border-white/10 hover:border-[#C9963F] px-3 py-2 text-xs text-white/70">
                  <span className="truncate">/q/{quote.public_token.slice(0, 12)}…</span><Copy size={12} />
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-[#0e0e0e] border border-white/10 p-6 sm:p-8 fade-up">
      <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-6">{title}</h2>{children}
    </section>
  );
}

function Row({ label, value }) {
  return (<div className="flex items-baseline justify-between py-1.5 text-sm"><span className="text-white/50">{label}</span><span className="text-white">{value}</span></div>);
}
